import { signal } from '@preact/signals'
import { useCallback } from 'preact/hooks'
import { transferRate } from './blockUpload'
import { uploadFileToBlob } from './directBlobUpload'

export type DirectBlobUploadStatus = 'uploading' | 'finishing' | 'failed' | 'done'

/** One browser-to-blob upload as the page shows it. */
export interface DirectBlobUpload {
    fileName: string
    totalBytes: number
    uploadedBytes: number
    bytesPerSecond: number | null
    secondsLeft: number | null
    status: DirectBlobUploadStatus
    /** Set when the status is `failed`. */
    error: string | null
}

export interface StartDirectBlobUploadOptions {
    contentType: string
    /** Runs once the blocks are committed, e.g. telling the API the file is in. If it fails, so does the upload. */
    onUploaded: () => Promise<void>
    /** Runs when the upload fails or is cancelled, so the API can forget the file it was waiting for. A failure reads `failed` once it settles. */
    onAbandoned: () => Promise<void>
}

/**
 * Uploads live here rather than in a component, keyed by the caller, so one keeps going while the person works
 * elsewhere in the app and is found again when they come back. Closing the browser tab still stops it.
 */
const uploads = signal<ReadonlyMap<string, DirectBlobUpload>>(new Map())
const controllers = new Map<string, AbortController>()

function put(key: string, upload: DirectBlobUpload | null) {
    const next = new Map(uploads.value)
    if (upload) next.set(key, upload)
    else next.delete(key)
    uploads.value = next
}

function patch(key: string, changes: Partial<DirectBlobUpload>) {
    const current = uploads.value.get(key)
    if (current) put(key, { ...current, ...changes })
}

function messageOf(error: unknown): string {
    return error instanceof Error && error.message ? error.message : 'The upload stopped.'
}

async function run(key: string, file: File, uploadUrl: string, options: StartDirectBlobUploadOptions, controller: AbortController) {
    const startedAt = Date.now()
    try {
        await uploadFileToBlob(file, uploadUrl, {
            contentType: options.contentType,
            signal: controller.signal,
            onProgress: uploadedBytes => patch(key, { uploadedBytes, ...transferRate(uploadedBytes, file.size, Date.now() - startedAt) }),
        })
        patch(key, { status: 'finishing', secondsLeft: null })
        await options.onUploaded()
        patch(key, { status: 'done' })
    } catch (error) {
        if (controller.signal.aborted) return
        // The API forgets the file before the failure is published, so whoever reacts to it can dismiss it (nothing is
        // running under the key any more) and rereads a server that no longer waits on the file.
        await options.onAbandoned().catch(() => { /* The failure goes on screen next; the API's copy is the caller's to reread. */ })
        if (controllers.get(key) !== controller) return
        controllers.delete(key)
        patch(key, { status: 'failed', error: messageOf(error) })
    } finally {
        if (controllers.get(key) === controller) controllers.delete(key)
    }
}

/** Starts an upload under `key`, replacing any finished one there. Refuses while one is still running. */
export function startDirectBlobUpload(key: string, file: File, uploadUrl: string, options: StartDirectBlobUploadOptions): void {
    if (controllers.has(key)) throw new Error(`An upload is already running for ${key}`)
    const controller = new AbortController()
    controllers.set(key, controller)
    put(key, {
        fileName: file.name, totalBytes: file.size, uploadedBytes: 0, bytesPerSecond: null, secondsLeft: null,
        status: 'uploading', error: null,
    })
    void run(key, file, uploadUrl, options, controller)
}

/** Stops the upload under `key` and forgets it; `onAbandoned` runs, and this resolves once it has. */
export async function cancelDirectBlobUpload(key: string, onAbandoned: () => Promise<void>): Promise<void> {
    controllers.get(key)?.abort(new DOMException('The upload was cancelled.', 'AbortError'))
    controllers.delete(key)
    put(key, null)
    await onAbandoned()
}

/** Forgets a finished or failed upload once the page has said what became of it. */
export function dismissDirectBlobUpload(key: string): void {
    if (!controllers.has(key)) put(key, null)
}

/**
 * The upload under `key`, and the controls for it. Re-renders on every block that lands. `key` names what the file
 * is for (one per web's recording, say), so a component mounted again finds the upload it started.
 */
export function useDirectBlobUpload(key: string) {
    const upload = uploads.value.get(key) ?? null
    const start = useCallback((file: File, uploadUrl: string, options: StartDirectBlobUploadOptions) =>
        startDirectBlobUpload(key, file, uploadUrl, options), [key])
    const cancel = useCallback((onAbandoned: () => Promise<void>) => cancelDirectBlobUpload(key, onAbandoned), [key])
    const dismiss = useCallback(() => dismissDirectBlobUpload(key), [key])
    return { upload, start, cancel, dismiss }
}
